/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Endpoint } from '../index';
import { z, ZodTypes } from '../zod';
import { GetClusterPgHba } from './cluster-pg-hba.get';

const Params = z.strictObject({
  organizationId: ZodTypes.Id,
  projectId: ZodTypes.Id,
  clusterId: ZodTypes.Id,
});

export const GetClusterDetails = {
  id: 'cluster-details.get',
  tag: 'Cluster',
  summary: 'Get cluster details',
  description: 'Retrieve the details of a cluster',
  method: 'get',
  flags: { isAuthorized: true },
  route: (params: z.infer<typeof Params>) =>
    `/organization/${params.organizationId}/project/${params.projectId}/cluster/${params.clusterId}/details`,
  request: {
    params: Params,
  },
  response: {
    200: {
      description: 'successful',
      content: {
        'application/json': {
          schema: z.strictObject({
            customerId: ZodTypes.Id,
            clusterId: ZodTypes.Id,
            replicaState: z.strictObject({
              replicas: z.number().min(0),
              readyReplicas: z.number().min(0),
              currentReplicas: z.number().min(0),
              updatedReplicas: z.number().min(0),
            }),
            name: z.string(),
            allowedSourceRanges: z.array(z.string().min(1)).nullable(),
            maintenanceWindows: z.array(z.string()).nullable(),
            customDbProfile: z.strictObject({
              cpu: z.number().min(0),
              memory: z.number().min(0),
              storage: z.number().min(0),
              storageType: z.string().min(1),
              masterLb: z.boolean(),
              replicaLb: z.boolean(),
            }),
            backupConfiguration: z.strictObject({
              logical: z.strictObject({
                enabled: z.boolean(),
                schedule: z.string().min(1),
                daysToRetain: z.number().min(0),
              }),
              continuous: z.strictObject({
                enabled: z.boolean(),
                schedule: z.string().min(1),
                daysToRetain: z.number().min(0),
              }),
            }),
            pgHba: GetClusterPgHba.response['200'].content['application/json'].schema,
            extension: z.string(),
          }),
        },
      },
    },
  },
} as const satisfies Endpoint;
